<template>
  <div class="bg-white rounded-lg shadow p-6">
    <h2 class="text-xl font-semibold mb-6">Billing Information</h2>

    <form @submit.prevent="handleSubmit">
      <!-- Loading -->
      <div v-if="isLoading" class="text-center py-8">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-2 text-gray-600">Loading billing information...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 0L6 8.586 4.707 7.293a1 1 0 00-1.414 1.414L4.586 10l-1.293 1.293a1 1 0 101.414 1.414L6 11.414l1.293 1.293a1 1 0 001.414-1.414L7.414 10l1.293-1.293a1 1 0 000-1.414z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-red-700">{{ error }}</p>
            <p v-if="stripeError" class="text-xs mt-1 text-red-600">{{ stripeError }}</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Contact Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="col-span-2 md:col-span-1">
            <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              v-model="formData.name"
              required
              class="w-full rounded-md border border-gray-200 p-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              :disabled="isSubmitting"
            >
          </div>

          <div class="col-span-2 md:col-span-1">
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              v-model="formData.email"
              required
              class="w-full rounded-md border border-gray-200 p-3 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              :disabled="isSubmitting"
            >
          </div>
        </div>

        <!-- Stripe Address Element -->
        <div class="mt-8 pt-6 border-t border-gray-200">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Billing Address</h3>

          <div v-if="isStripeReady">
            <div ref="stripeAddressEl" class="mb-4"></div>
            <p v-if="addressError" class="mt-2 text-sm text-red-600">{{ addressError }}</p>
          </div>
          <div v-else>
            <p class="text-sm text-gray-500">Loading address form...</p>
          </div>
        </div>

        <!-- Submit -->
        <div class="mt-8 flex justify-end">
          <button
            type="submit"
            :disabled="isSubmitting || isLoading || !addressElement"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isSubmitting">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { mapActions } from 'vuex';

export default {
  name: 'BillingInformation',
  created() {

    // Vue does not proxy keys beginning with an underscore, so these were

    // never reactive state. They are plain instance properties.

    this._addressReady = false;

    this._prefillAddress = null;

  },
  data() {
    return {
      formData: { name: '', email: '', phone: '' },

      isLoading: true,
      isSubmitting: false,
      error: null,
      addressError: null,
      stripeError: null,
      isStripeReady: false,

      stripe: null,
      elements: null,
      addressElement: null,

    };
  },

  async mounted() {
    try {
      this.isLoading = true;
      this.stripe = await loadStripe(window.STRIPE_PUBLISHABLE_KEY);
      if (!this.stripe) throw new Error('Failed to initialize Stripe');

      await this.loadBillingInfo();
      this.isStripeReady = true;
    } catch (err) {
      console.error('Error during mounted:', err);
      this.stripeError = err?.message || 'Stripe init failed';
      this.error = 'Failed to initialize payment form. Please refresh and try again.';
    } finally {
      this.isLoading = false;
      await this.$nextTick();
      if (!this.error) {
        try {
          await this.initializeStripeElements();
        } catch (err) {
          this.stripeError = err?.message || 'Unable to render address form';
        }
      }
    }
  },

  beforeUnmount() {
    try {
      this.addressElement?.unmount?.();
      this.addressElement = null;
      this.elements = null;
    } catch {
      // Teardown must not throw: the element may already be unmounted.
    }
  },

  methods: {
    ...mapActions('billing', {
      fetchBillingInfo: 'fetchBillingInfo',
      updateBillingInfo: 'updateBillingInfo'
    }),

    async loadBillingInfo() {
      const api = await this.fetchBillingInfo();
      const payload = api?.data;
      if (!payload) return;

      this.formData = {
        name:  payload.name  ?? '',
        email: payload.email ?? '',
        phone: payload.phone ?? '',
      };

      const a = payload.address ?? {};
      this._prefillAddress = {
        name: this.formData.name || '',
        phone: this.formData.phone || '',
        address: {
          line1: a.line1 || '',
          line2: a.line2 || '',
          city: a.city || '',
          state: a.state || '',
          postal_code: a.postal_code || a.postalCode || '',
          country: a.country || 'US',
        },
      };

      if (this.addressElement) {
        this.updateAddressElement();
      }
    },

    async initializeStripeElements() {
      if (!this.$refs.stripeAddressEl) {
        await this.$nextTick();
      }

      this.elements = this.stripe.elements({
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#3b82f6',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, sans-serif',
          },
        },
      });

      this.addressElement = this.elements.create('address', {
        mode: 'billing',
        fields: { phone: 'always' },
        defaultValues: this._prefillAddress || {},
      });

      this.addressElement.on('ready', () => { this._addressReady = true; });
      this.addressElement.on('change', (event) => {
        this.addressError = event.error?.message || null;
      });

      this.addressElement.mount(this.$refs.stripeAddressEl);
    },

    async updateAddressElement() {
      if (!this.addressElement || !this._prefillAddress) return;

      const addressData = {
        name: this._prefillAddress.name || '',
        phone: this.formatPhoneNumber(this._prefillAddress.phone || ''),
        address: {
          line1: this._prefillAddress.address?.line1 ?? '',
          line2: this._prefillAddress.address?.line2 ?? '',
          city: this._prefillAddress.address?.city ?? '',
          state: this._prefillAddress.address?.state ?? '',
          postal_code: this._prefillAddress.address?.postal_code ?? '',
          country: this._prefillAddress.address?.country ?? 'US',
        },
      };

      if (!this._addressReady) {
        await new Promise((resolve) => {
          this.addressElement.on('ready', () => {
            this._addressReady = true;
            resolve();
          });
        });
      }

      this.addressElement.update({ defaultValues: addressData });
    },

    async handleSubmit() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.error = null;
      this.addressError = null;

      try {
        if (!this.addressElement) {
          throw new Error('Address form not ready. Please try refreshing the page.');
        }

        const addressValue = await this.addressElement.getValue();
        if (!addressValue || !addressValue.complete) {
          this.addressError = 'Please complete all required address fields.';
          return;
        }

        const address = addressValue.value?.address || addressValue.address || {};
        const billingData = {
          name: this.formData.name?.trim() || '',
          email: this.formData.email?.trim() || '',
          phone: this.formData.phone?.trim() || '',
          address: {
            line1: address.line1?.trim() || '',
            line2: address.line2?.trim() || '',
            city: address.city?.trim() || '',
            state: address.state?.trim() || '',
            postal_code: address.postal_code?.trim() || '',
            country: address.country?.trim() || '',
          }
        };

        await this.updateBillingInfo(billingData);
        this.$toast?.success?.('Billing information updated successfully');
      } catch (err) {
        console.error('Error updating billing info:', err);
        this.error = err?.response?.data?.message || err?.message || 'Failed to update billing information. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    },

    formatPhoneNumber(phone) {
      if (!phone) return '';
      const cleaned = ('' + phone).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
    },
  },
};
</script>

<style scoped>
:deep(.Address) {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}
:deep(.Address--focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}
:deep(.Address--invalid) {
  border-color: #ef4444;
}
</style>
